import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, Copy, RefreshCw, Key } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useUserRoles } from "@/hooks/useUserRoles";

interface ServiceAccount {
  id: string;
  name: string;
  api_key: string;
  permissions: string[];
  created_at: string;
}

export const ServiceAccountManager = () => {
  const [serviceAccounts, setServiceAccounts] = useState<ServiceAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [newAccountName, setNewAccountName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();
  const [authToken, setAuthToken] = useState<string | null>(null);

  useEffect(() => {
    const getAuthToken = async () => {
      const { data } = await supabase.auth.getSession();
      setAuthToken(data.session?.access_token || null);
    };
    
    getAuthToken();
  }, []);

  useEffect(() => {
    if (!isAdmin || !authToken) return;
    fetchServiceAccounts();
  }, [isAdmin, authToken]);

  const fetchServiceAccounts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('get_service_accounts', {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (error) {
        console.error("Error fetching service accounts:", error);
        throw error;
      }
      
      const formattedAccounts = (data || []).map((account: any) => ({
        id: account.id,
        name: account.name,
        api_key: account.api_key,
        permissions: account.permissions || [],
        created_at: account.created_at
      }));
      
      setServiceAccounts(formattedAccounts);
    } catch (error: any) {
      console.error("Error fetching service accounts:", error);
      toast({
        title: "Error",
        description: "Failed to load service accounts. Please ensure you're logged in with admin privileges.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const generateApiKey = () => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const createServiceAccount = async () => {
    if (!authToken) {
      toast({
        title: "Error",
        description: "Authentication token not available. Please try logging in again.",
        variant: "destructive"
      });
      return;
    }

    try {
      if (!newAccountName.trim()) {
        toast({
          title: "Validation Error",
          description: "Please enter a name for the service account",
          variant: "destructive"
        });
        return;
      }

      setIsCreating(true);
      const apiKey = generateApiKey();

      const { data, error } = await supabase.functions.invoke('create_service_account', {
        body: {
          p_name: newAccountName.trim(),
          p_api_key: apiKey
        },
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Service account created successfully"
      });

      fetchServiceAccounts();
      setNewAccountName("");
      setIsDialogOpen(false);
    } catch (error: any) {
      console.error("Error creating service account:", error);
      toast({
        title: "Error",
        description: "Failed to create service account",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  const deleteServiceAccount = async (id: string) => {
    if (!authToken) {
      toast({
        title: "Error",
        description: "Authentication token not available. Please try logging in again.",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase.functions.invoke('delete_service_account', {
        body: {
          p_id: id
        },
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (error) throw error;

      setServiceAccounts(serviceAccounts.filter(account => account.id !== id));
      toast({
        title: "Success",
        description: "Service account deleted successfully"
      });
    } catch (error: any) {
      console.error("Error deleting service account:", error);
      toast({
        title: "Error",
        description: "Failed to delete service account",
        variant: "destructive"
      });
    }
  };

  const regenerateApiKey = async (id: string) => {
    if (!authToken) {
      toast({
        title: "Error",
        description: "Authentication token not available. Please try logging in again.",
        variant: "destructive"
      });
      return;
    }

    try {
      const newApiKey = generateApiKey();
      
      const { error } = await supabase.functions.invoke('regenerate_api_key', {
        body: {
          p_id: id,
          p_new_key: newApiKey
        },
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (error) throw error;

      setServiceAccounts(
        serviceAccounts.map(account => 
          account.id === id ? { ...account, api_key: newApiKey } : account
        )
      );

      toast({
        title: "Success",
        description: "API key regenerated successfully"
      });
    } catch (error: any) {
      console.error("Error regenerating API key:", error);
      toast({
        title: "Error",
        description: "Failed to regenerate API key",
        variant: "destructive"
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast({
          title: "Copied",
          description: "API key copied to clipboard"
        });
      },
      err => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-xl">Service Accounts</CardTitle>
        <CardDescription>
          Manage API access for external notification systems.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : serviceAccounts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No service accounts found. Create one to enable integrations.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {serviceAccounts.map(account => (
              <div key={account.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-base">{account.name}</h3>
                    <p className="text-sm text-muted-foreground">Created: {new Date(account.created_at).toLocaleDateString()}</p>
                    <div className="mt-2">
                      {account.permissions.map(perm => (
                        <Badge key={perm} variant="outline" className="mr-2 mb-2">
                          {perm}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => copyToClipboard(account.api_key)}
                      title="Copy API key"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => regenerateApiKey(account.id)}
                      title="Regenerate API key"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => deleteServiceAccount(account.id)}
                      title="Delete account"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3">
                  <Label htmlFor={`api-key-${account.id}`} className="text-xs text-muted-foreground">
                    API Key
                  </Label>
                  <div className="flex items-center mt-1 bg-muted/50 rounded p-2">
                    <Key className="h-4 w-4 text-muted-foreground mr-2" />
                    <code className="text-xs font-mono truncate flex-1">
                      {account.api_key}
                    </code>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter className="justify-end">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Service Account
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Service Account</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="account-name">Account Name</Label>
              <Input
                id="account-name"
                placeholder="Enter account name"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                className="mt-1"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={createServiceAccount} 
                disabled={isCreating || !newAccountName.trim()}
              >
                {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardFooter>
    </Card>
  );
};
